import abstractModel from "../../core/MercatelyModel";
import { MERCATELY_API, MERCATELY_API_V2 } from '../../constants/AppConstants';
import {
  microserviceChatbotsCache,
  msChatbotsApiUrl,
  buildMsChatbotsAuthHeaders
} from '../../services/chatbots/dualApiMergeChatbotService';

const baseResource = `${MERCATELY_API_V2}/chatbots`;

const formatNewResource = (chatbotWebId) => {
  return `${baseResource}/${chatbotWebId}/chatbot_options`;
};

const model = abstractModel('');

const parentGet = model.get;
const parentSave = model.save;
const parentRemove = model.remove;

function normalizeArrays(target) {
  if (!target || typeof target !== 'object') return;

  const arrayFields = [
    'options_list',
    'chat_bot_actions_attributes',
    'option_sub_lists_attributes',
    'tag_ids',
    'schedule_weekdays'
  ];

  for (const key of Object.keys(target)) {
    const val = target[key];
    if (val && typeof val === 'object') {
      if (arrayFields.includes(key)) {
        if (!Array.isArray(val)) {
          target[key] = Object.keys(val)
            .sort((a, b) => parseInt(a, 10) - parseInt(b, 10))
            .map((k) => val[k]);
        } else {
          target[key] = val.filter((item) => item !== undefined);
        }
      }
      normalizeArrays(target[key]);
    }
  }
}

// Convert FormData back to a nested object structure matching what the backend expects
function formDataToNestedObject(formData) {
  const obj = {};

  for (let [key, value] of formData.entries()) {
    key = key.replace(/\]\[\]\[\]/g, '][]');

    if (!key.startsWith('chatbot_option[')) {
      if (key === 'id') {
        // The microservice keys chatbot options by UUID, so the id must travel as-is.
        obj.id = value;
      }
      continue;
    }

    const parts = key.split('[').map((part, index) => {
      if (index === 0) return part;
      return part.endsWith(']') ? part.slice(0, -1) : part;
    });

    let current = obj;
    for (let i = 1; i < parts.length; i++) {
      const part = parts[i];
      const nextPart = parts[i + 1];

      const val = value === 'true' ? true : value === 'false' ? false : value;

      if (nextPart === undefined) {
        if (part !== '') {
          current[part] = val;
        }
      } else if (nextPart === '') {
        if (current[part] === undefined) {
          current[part] = [];
        }

        const afterEmpty = parts[i + 2];
        if (afterEmpty === undefined) {
          current[part].push(val);
          break;
        } else {
          const array = current[part];
          let lastObj = array[array.length - 1];

          if (!lastObj || (lastObj[afterEmpty] !== undefined && afterEmpty !== 'tag_ids' && afterEmpty !== 'schedule_weekdays')) {
            lastObj = {};
            array.push(lastObj);
          }

          current = lastObj;
          i++;
        }
      } else if (/^\d+$/.test(nextPart)) {
        if (!Array.isArray(current[part])) {
          current[part] = [];
        }
        const index = parseInt(nextPart, 10);
        if (!current[part][index]) {
          current[part][index] = {};
        }
        current = current[part][index];
        i++;
      } else {
        if (current[part] === undefined) {
          current[part] = {};
        }
        current = current[part];
      }
    }
  }

  normalizeArrays(obj);
  return obj;
}

model.get = async (chatbotWebId, isFromMicroservice) => {
  if (isFromMicroservice || microserviceChatbotsCache.has(chatbotWebId)) {
    const endpoint = msChatbotsApiUrl(`/chatbots/${chatbotWebId}/chatbot_options`);
    const headers = await buildMsChatbotsAuthHeaders();
    const res = await fetch(endpoint, { headers });
    if (!res.ok) throw new Error('Error al obtener las opciones');
    return res.json();
  }

  const resource = formatNewResource(chatbotWebId);
  return parentGet({}, { path: resource });
};

model.save = async (chatbotWebId, params, isFromMicroservice, rawData) => {
  if (isFromMicroservice || microserviceChatbotsCache.has(chatbotWebId)) {
    const endpoint = msChatbotsApiUrl(`/chatbots/${chatbotWebId}/chatbot_options`);
    const headers = await buildMsChatbotsAuthHeaders();

    let attachedFileMetadata = null;
    const fileToUpload = params instanceof FormData ? params.get('chatbot_option[file]') : null;

    if (fileToUpload && (fileToUpload instanceof File || fileToUpload instanceof Blob) && fileToUpload.size > 0) {
      const uploadEndpoint = msChatbotsApiUrl(`/chatbots/${chatbotWebId}/chatbot_options/upload`);
      const uploadHeaders = await buildMsChatbotsAuthHeaders();
      delete uploadHeaders['Content-Type'];

      const uploadFormData = new FormData();
      uploadFormData.append('file', fileToUpload);

      const uploadRes = await fetch(uploadEndpoint, {
        method: 'POST',
        headers: uploadHeaders,
        body: uploadFormData
      });

      if (!uploadRes.ok) {
        const errData = await uploadRes.json().catch(() => ({}));
        throw new Error(errData.error?.message || errData.message || 'Error al subir el archivo al microservicio');
      }

      attachedFileMetadata = await uploadRes.json();
    }

    // Parse FormData parameters back to JSON
    let parsedData = rawData ? (rawData.chatbot_option ? { ...rawData.chatbot_option } : { ...rawData }) : (params instanceof FormData ? formDataToNestedObject(params) : params);

    if (parsedData.file) {
      delete parsedData.file;
    }

    if (attachedFileMetadata) {
      parsedData.attached_file = {
        ...attachedFileMetadata,
        metadata: { identified: true }
      };
    }

    const res = await fetch(endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify(parsedData)
    });
    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      throw new Error(errData.error?.message || errData.message || 'Error al guardar la opción');
    }
    return res.json();
  }

  const resource = formatNewResource(chatbotWebId);
  return parentSave(params, { path: resource });
};

model.remove = async (chatbotWebId, params, isFromMicroservice) => {
  if (isFromMicroservice || microserviceChatbotsCache.has(chatbotWebId)) {
    const optionId = params?.id;
    const endpoint = msChatbotsApiUrl(`/chatbots/${chatbotWebId}/chatbot_options/${optionId}`);
    const headers = await buildMsChatbotsAuthHeaders();
    delete headers['Content-Type']; // DELETE request does not need body/Content-Type in Fastify

    const res = await fetch(endpoint, {
      method: 'DELETE',
      headers
    });
    if (!res.ok) throw new Error('Error al eliminar la opción');
    return res.json();
  }

  const resource = formatNewResource(chatbotWebId);
  return parentRemove(params, { path: resource });
};

model.getCustomerColumns = async () => {
  const resource = `${MERCATELY_API}/whatsapp_templates/customer_columns`;
  return parentGet({}, { path: resource });
};

model.getTeamAssignments = async (chatbotWebId, isFromMicroservice) => {
  const resource = `${formatNewResource(chatbotWebId)}/team_assignments`;
  return parentGet({}, { path: resource });
};

model.getPathOptions = async (chatbotWebId, optionId, isFromMicroservice) => {
  if (isFromMicroservice || microserviceChatbotsCache.has(chatbotWebId)) {
    return { option: null };
  }

  const resource = `/retailers/chat_bots/${chatbotWebId}/path_option?option_id=${optionId}`;
  return parentGet({}, { path: resource });
};

model.testAPI = async (params = {}) => {
  const resource = `${MERCATELY_API_V2}/api_options/test_api`;
  return parentSave(params, { path: resource });
};

export default model;
